import { createContext, useContext, useMemo, useState } from "react";
import { createStore, useStore } from "zustand";
import { combine } from "zustand/middleware";

import { TaskSet } from "@/core/types";

import { SetType } from "../TaskForm/getSetTypeChoices";

export type CalendarContextMenuData = {
  date?: string;
  taskSet?: TaskSet;
  x: number;
  y: number;
};

export type NewSetValues = {
  date?: string;
  type?: SetType;
  competition?: string | null;
};

const createCalendarStore = () =>
  createStore(
    combine(
      {
        contextMenu: null as CalendarContextMenuData | null,
        newSetValues: null as NewSetValues | null,
        editingSetUuid: null as string | null,
      },
      (set) => ({
        setContextMenu: (menuData: CalendarContextMenuData | null) =>
          set({ contextMenu: menuData }),
        setNewSetValues: (newSetValues: NewSetValues | null) => set({ newSetValues }),
        setEditingSetUuid: (editingSetUuid: string | null) => set({ editingSetUuid }),
      }),
    ),
  );

type Context = ReturnType<typeof createCalendarStore>;

const CalendarContext = createContext<Context | undefined>(undefined);

export const CalendarContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [store] = useState(createCalendarStore);
  return <CalendarContext.Provider value={store}>{children}</CalendarContext.Provider>;
};

export const useVirtualElement = (contextMenu: CalendarContextMenuData | null) => {
  return useMemo(() => {
    if (!contextMenu) {
      return undefined;
    }
    const getBoundingClientRect = () => ({
      top: contextMenu.y,
      left: contextMenu.x,
      right: contextMenu.x,
      bottom: contextMenu.y,
      width: 0,
      height: 0,
      x: contextMenu.x,
      y: contextMenu.y,
      toJSON: () => "",
    });
    return {
      getBoundingClientRect,
    };
  }, [contextMenu]);
};

export const useCalendar = () => {
  const store = useContext(CalendarContext);
  if (store === undefined) {
    throw new Error("useCalendarContextMenu must be used within a CalendarContextMenuProvider");
  }
  return useStore(store);
};
